import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { authAPI, userAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState('cliente');

  const checkAuth = useCallback(async () => {
    try {
      const token = Cookies.get('session_token') || localStorage.getItem('session_token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await authAPI.getMe();
      setUser(response.data);
      
      // Set active role based on user role
      const role = response.data.role;
      if (role === 'ambos') {
        const savedRole = localStorage.getItem('activeRole');
        setActiveRole(savedRole || 'cliente');
      } else {
        setActiveRole(role === 'admin' ? 'admin' : role);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      Cookies.remove('session_token');
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { user: userData, session_token } = response.data;
    
    Cookies.set('session_token', session_token, { expires: 7 });
    localStorage.setItem('session_token', session_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setActiveRole(userData.role === 'ambos' ? 'cliente' : userData.role);
    
    return userData;
  };

  const register = async (email, password, name, legal = {}) => {
    const response = await authAPI.register({
      email,
      password,
      name,
      accepted_terms: !!legal.accepted_terms,
      accepted_privacy: !!legal.accepted_privacy,
      accepted_data_deletion: !!legal.accepted_data_deletion,
    });
    const { user: userData, session_token } = response.data;
    
    Cookies.set('session_token', session_token, { expires: 7 });
    localStorage.setItem('session_token', session_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setActiveRole('cliente');
    
    return userData;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await authAPI.processSession(sessionId);
    const { user: userData, session_token } = response.data;
    
    Cookies.set('session_token', session_token, { expires: 7 });
    localStorage.setItem('session_token', session_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setActiveRole(userData.role === 'ambos' ? 'cliente' : userData.role);
    
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      Cookies.remove('session_token');
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeRole');
      setUser(null);
      setActiveRole('cliente');
    }
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
    localStorage.setItem('user', JSON.stringify({ ...user, ...updatedData }));
  };

  const switchRole = (role) => {
    if (user?.role === 'ambos' && ['cliente', 'proveedor'].includes(role)) {
      setActiveRole(role);
      localStorage.setItem('activeRole', role);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await userAPI.getProfile();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    loading,
    activeRole,
    isAuthenticated: !!user,
    isClient: activeRole === 'cliente' || user?.role === 'cliente',
    isProvider: activeRole === 'proveedor' || user?.role === 'proveedor',
    isAdmin: user?.role === 'admin',
    isBoth: user?.role === 'ambos',
    login,
    register,
    loginWithGoogle,
    logout,
    updateUser,
    switchRole,
    refreshUser,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
